<template>
  <!-- aria-live on the container, not on each toast: a region that is already in the DOM
       is what screen readers watch, so an item added to it is announced. -->
  <div class="vp-toast-stack" aria-live="polite" aria-atomic="false">
    <TransitionGroup name="vp-toast">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="vp-toast"
        :class="`vp-toast--${toast.variant}`"
        :role="toast.variant === 'danger' ? 'alert' : 'status'"
        @mouseenter="pause(toast)"
        @mouseleave="resume(toast)"
      >
        <span class="vp-toast-icon">
          <i :class="VARIANTS[toast.variant].icon"></i>
        </span>
        <div class="vp-toast-content">
          <div class="vp-toast-title">{{ $t(VARIANTS[toast.variant].titleKey) }}</div>
          <div class="vp-toast-message">{{ toast.message }}</div>
        </div>
        <button
          type="button"
          class="vp-toast-close"
          :aria-label="$t('common.close')"
          @click="dismiss(toast.id)"
        >
          <i class="ph-bold ph-x"></i>
        </button>
        <span
          class="vp-toast-timer"
          :class="{ 'is-paused': toast.paused }"
          :style="{ animationDuration: `${toast.duration}ms` }"
        ></span>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
// 1. Vue core
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'

// 5. Constants
const DEFAULT_DURATION = 5000
// Older toasts beyond this are dropped: a burst of errors should not wall off the page.
const MAX_VISIBLE = 4

const VARIANTS = {
  success: { icon: 'ph-fill ph-check-circle', titleKey: 'common.notification.success' },
  danger: { icon: 'ph-fill ph-x-circle', titleKey: 'common.notification.error' },
  warning: { icon: 'ph-fill ph-warning', titleKey: 'common.notification.warning' },
  info: { icon: 'ph-fill ph-info', titleKey: 'common.notification.info' }
}

const TYPE_TO_VARIANT = { error: 'danger' }

const store = useStore()

const toasts = ref([])
const current = computed(() => store.state.notification)

// Timers live outside the reactive list; only their bookkeeping is on the toast.
const timers = new Map()

const clearTimer = (id) => {
  clearTimeout(timers.get(id))
  timers.delete(id)
}

const dismiss = (id) => {
  clearTimer(id)
  toasts.value = toasts.value.filter(toast => toast.id !== id)
}

const schedule = (toast) => {
  toast.startedAt = Date.now()
  timers.set(toast.id, setTimeout(() => dismiss(toast.id), toast.remaining))
}

// Hovering holds the toast so a long error can be read to the end.
const pause = (toast) => {
  if (toast.paused) return
  clearTimer(toast.id)
  toast.remaining -= Date.now() - toast.startedAt
  toast.paused = true
}

const resume = (toast) => {
  if (!toast.paused) return
  toast.paused = false
  schedule(toast)
}

/*
 * The store keeps a single "current" notification and clears it on its own timer. The
 * stack copies each one in by id instead of rendering it directly, so a second message
 * no longer replaces the first mid-read, and the store clearing it does not cut a
 * toast short.
 */
watch(current, (notification) => {
  if (!notification?.message) return
  if (toasts.value.some(toast => toast.id === notification.id)) return

  const variant = TYPE_TO_VARIANT[notification.type] ?? notification.type
  const duration = notification.timeout || DEFAULT_DURATION
  const toast = {
    id: notification.id,
    message: notification.message,
    variant: VARIANTS[variant] ? variant : 'info',
    duration,
    remaining: duration,
    paused: false,
    startedAt: 0
  }

  toasts.value = [...toasts.value, toast].slice(-MAX_VISIBLE)
  // Schedule the reactive copy, so pause/resume and the timer touch the same object.
  schedule(toasts.value.at(-1))
  for (const id of timers.keys()) {
    if (!toasts.value.some(item => item.id === id)) clearTimer(id)
  }
})

onBeforeUnmount(() => {
  timers.forEach(timer => clearTimeout(timer))
  timers.clear()
})
</script>

<style scoped>
.vp-toast-stack {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1090;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 360px;
  max-width: calc(100vw - 32px);
  pointer-events: none;
}

.vp-toast {
  --vp-toast-accent: var(--vp-info);
  --vp-toast-tint: var(--vp-light-info);

  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 12px 16px 16px;
  background: #fff;
  border: 1px solid var(--vp-gray-200);
  border-left: 4px solid var(--vp-toast-accent);
  border-radius: var(--vp-radius);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  pointer-events: auto;
}

.vp-toast--success {
  --vp-toast-accent: var(--vp-success);
  --vp-toast-tint: var(--vp-light-success);
}

.vp-toast--danger {
  --vp-toast-accent: var(--vp-danger);
  --vp-toast-tint: var(--vp-light-danger);
}

.vp-toast--warning {
  --vp-toast-accent: var(--vp-warning);
  --vp-toast-tint: var(--vp-light-warning);
}

.vp-toast-icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: var(--vp-toast-accent);
  background: var(--vp-toast-tint);
}

.vp-toast-content {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 1px;
}

.vp-toast-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--vp-gray-900);
  line-height: 1.3;
}

.vp-toast-message {
  margin-top: 2px;
  font-size: 0.8125rem;
  color: var(--vp-gray-700);
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.vp-toast-close {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--vp-gray-500);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.vp-toast-close:hover,
.vp-toast-close:focus-visible {
  background: var(--vp-gray-100);
  color: var(--vp-gray-800);
}

/* Remaining time, drawn as a shrinking line along the bottom edge. */
.vp-toast-timer {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  width: 100%;
  background: var(--vp-toast-accent);
  opacity: 0.35;
  transform-origin: left center;
  animation-name: vp-toast-countdown;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

.vp-toast-timer.is-paused {
  animation-play-state: paused;
}

@keyframes vp-toast-countdown {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}

.vp-toast-enter-active,
.vp-toast-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.vp-toast-enter-from,
.vp-toast-leave-to {
  opacity: 0;
  transform: translateX(24px);
}

.vp-toast-move {
  transition: transform 0.25s ease;
}

/* Leaving items are taken out of the flow so the rest slide up smoothly. */
.vp-toast-leave-active {
  position: absolute;
  width: 100%;
}

@media (max-width: 575.98px) {
  .vp-toast-stack {
    top: 12px;
    right: 16px;
    left: 16px;
    width: auto;
    max-width: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .vp-toast-enter-active,
  .vp-toast-leave-active,
  .vp-toast-move {
    transition: none;
  }

  .vp-toast-timer {
    animation: none;
  }
}
</style>
